import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box, Flex, HStack, VStack, Text, Badge, Button, IconButton, Select, Spinner, Tooltip,
    Table, Thead, Tbody, Tr, Th, Td, TableContainer, useToast, useBreakpointValue,
    Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody, DrawerCloseButton, Divider,
} from '@chakra-ui/react';
import { FaCopy, FaExternalLinkAlt, FaSyncAlt, FaChevronLeft, FaChevronRight, FaInbox } from 'react-icons/fa';
import dayjs from 'dayjs';
import { listarConsultasEstoque, getConsultaEstoque } from '../services/api';

const STATUS = {
    aberta: { rotulo: 'Aberta', cor: 'blue', dica: 'O fornecedor ainda pode responder' },
    respondida: { rotulo: 'Respondida', cor: 'green', dica: 'O fornecedor enviou as respostas' },
    expirada: { rotulo: 'Expirada', cor: 'gray', dica: 'Passou da validade sem o fornecedor enviar' },
};

const RESULTADO = {
    reativado: { rotulo: 'Reativado', cor: 'green' },
    bloqueio_permanente: { rotulo: 'Bloqueio permanente', cor: 'red' },
    ensaio: { rotulo: 'Ensaio', cor: 'orange' },
    sem_efeito: { rotulo: 'Sem efeito', cor: 'gray' },
};

const dataHora = (d) => (d ? dayjs(d).format('DD/MM/YYYY HH:mm') : '-');
const data = (d) => (d ? dayjs(d).format('DD/MM/YYYY') : '-');

function BadgeStatus({ status }) {
    const meta = STATUS[status] || { rotulo: status, cor: 'gray', dica: '' };
    return (
        <Tooltip label={meta.dica} hasArrow isDisabled={!meta.dica}>
            <Badge colorScheme={meta.cor} borderRadius="md" px={2}>{meta.rotulo}</Badge>
        </Tooltip>
    );
}

// O progresso é o que diz se vale a pena cobrar o fornecedor: sem ele a linha
// não distingue "mandei agora" de "mandei e ninguém abriu".
function Progresso({ respondidos, total }) {
    const completo = total > 0 && respondidos >= total;
    return (
        <HStack spacing={1}>
            <Text fontWeight="bold" color={completo ? 'green.600' : (respondidos ? 'orange.500' : 'gray.400')}>
                {respondidos}
            </Text>
            <Text color="gray.400">/ {total}</Text>
        </HStack>
    );
}

export default function ConsultasEstoqueTab({ fornecedores = [] }) {
    const toast = useToast();
    const compacto = useBreakpointValue({ base: true, lg: false });

    const [itens, setItens] = useState([]);
    const [meta, setMeta] = useState({ pagina: 1, total: 0, ultima_pagina: 1 });
    const [carregando, setCarregando] = useState(true);
    const [fornecedorId, setFornecedorId] = useState('');
    const [status, setStatus] = useState('');
    const [pagina, setPagina] = useState(1);

    const [detalhe, setDetalhe] = useState(null);
    const [carregandoDetalhe, setCarregandoDetalhe] = useState(false);

    const carregar = useCallback(async () => {
        setCarregando(true);
        const r = await listarConsultasEstoque({
            fornecedor_id: fornecedorId || undefined,
            status: status || undefined,
            pagina,
            por_pagina: 25,
        });
        setItens(r?.data || []);
        setMeta(r?.meta || { pagina: 1, total: 0, ultima_pagina: 1 });
        setCarregando(false);
    }, [fornecedorId, status, pagina]);

    useEffect(() => { carregar(); }, [carregar]);

    const copiar = async (e, url) => {
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(url);
            toast({ title: 'Link copiado.', status: 'success', duration: 2000, isClosable: true });
        } catch {
            toast({ title: 'Não foi possível copiar o link.', status: 'error', isClosable: true });
        }
    };

    const abrirLink = (e, url) => {
        e.stopPropagation();
        window.open(url, '_blank', 'noopener');
    };

    const abrirDetalhe = async (consulta) => {
        setDetalhe({ id: consulta.id, fornecedor: consulta.fornecedor });
        setCarregandoDetalhe(true);
        const r = await getConsultaEstoque(consulta.id);
        setDetalhe(r || null);
        setCarregandoDetalhe(false);
    };

    const opcoesFornecedor = useMemo(() => (
        (fornecedores || []).map((f) => ({ id: String(f.fornecedor_id), nome: f.nome }))
    ), [fornecedores]);

    const trocar = (setter) => (e) => { setPagina(1); setter(e.target.value); };

    const acoes = (c) => (
        <HStack spacing={1} justify="flex-end">
            <Tooltip label="Copiar o link do fornecedor" hasArrow>
                <IconButton
                    aria-label="Copiar link" icon={<FaCopy />} size="sm" variant="ghost"
                    onClick={(e) => copiar(e, c.url)}
                />
            </Tooltip>
            <Tooltip label="Abrir a tela do fornecedor" hasArrow>
                <IconButton
                    aria-label="Abrir" icon={<FaExternalLinkAlt />} size="sm" variant="ghost"
                    onClick={(e) => abrirLink(e, c.url)}
                />
            </Tooltip>
        </HStack>
    );

    return (
        <>
            <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={4} mb={4} boxShadow="sm">
                <Flex gap={3} wrap="wrap" align="center">
                    <Select
                        size="sm" maxW={{ base: '100%', md: '240px' }} borderRadius="md"
                        value={fornecedorId} onChange={trocar(setFornecedorId)}
                    >
                        <option value="">Todos os fornecedores</option>
                        {opcoesFornecedor.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
                    </Select>

                    <Select
                        size="sm" maxW={{ base: '100%', md: '200px' }} borderRadius="md"
                        value={status} onChange={trocar(setStatus)}
                    >
                        <option value="">Todos os status</option>
                        <option value="aberta">Aberta</option>
                        <option value="respondida">Respondida</option>
                        <option value="expirada">Expirada</option>
                    </Select>

                    <Button size="sm" variant="outline" leftIcon={<FaSyncAlt />} onClick={carregar} isLoading={carregando}>
                        Atualizar
                    </Button>
                </Flex>
            </Box>

            <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" boxShadow="sm" overflow="hidden">
                {carregando ? (
                    <Flex justify="center" py={12}><Spinner color="orange.400" /></Flex>
                ) : itens.length === 0 ? (
                    <VStack py={12} spacing={2} color="gray.500">
                        <Box fontSize="2xl"><FaInbox /></Box>
                        <Text fontWeight="medium">Nenhuma consulta por aqui</Text>
                        <Text fontSize="sm">Gere uma na aba Produtos bloqueados, escolhendo um fornecedor.</Text>
                    </VStack>
                ) : compacto ? (
                    <VStack align="stretch" spacing={0} divider={<Divider />}>
                        {itens.map((c) => (
                            <Box key={c.id} p={4} onClick={() => abrirDetalhe(c)} cursor="pointer" _active={{ bg: 'orange.50' }}>
                                <Flex justify="space-between" align="start" gap={2}>
                                    <Box minW={0}>
                                        <Text fontWeight="bold" color="gray.800">{c.fornecedor}</Text>
                                        <Text fontSize="xs" color="gray.500">
                                            {dataHora(c.criada_em)} · {c.criada_por || 'automático'}
                                        </Text>
                                    </Box>
                                    {acoes(c)}
                                </Flex>
                                <HStack mt={2} spacing={2} wrap="wrap">
                                    <BadgeStatus status={c.status} />
                                    {c.ensaio && <Badge colorScheme="orange" variant="outline" borderRadius="md">Ensaio</Badge>}
                                    <Progresso respondidos={c.respondidos} total={c.total_itens} />
                                    <Text fontSize="xs" color="gray.500">respondidos</Text>
                                </HStack>
                            </Box>
                        ))}
                    </VStack>
                ) : (
                    <TableContainer>
                        <Table size="sm" variant="simple">
                            <Thead bg="gray.50">
                                <Tr>
                                    <Th>Fornecedor</Th>
                                    <Th>Respostas</Th>
                                    <Th>Status</Th>
                                    <Th>Criada</Th>
                                    <Th>Prazo</Th>
                                    <Th isNumeric>Ações</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {itens.map((c) => (
                                    <Tr
                                        key={c.id} cursor="pointer" _hover={{ bg: 'orange.50' }}
                                        onClick={() => abrirDetalhe(c)}
                                    >
                                        <Td>
                                            <Text fontWeight="bold" color="gray.800">{c.fornecedor}</Text>
                                            <Text fontSize="xs" color="gray.500">consulta #{c.id}</Text>
                                        </Td>
                                        <Td><Progresso respondidos={c.respondidos} total={c.total_itens} /></Td>
                                        <Td>
                                            <HStack spacing={1}>
                                                <BadgeStatus status={c.status} />
                                                {c.ensaio && <Badge colorScheme="orange" variant="outline" borderRadius="md">Ensaio</Badge>}
                                            </HStack>
                                        </Td>
                                        <Td>
                                            <Text fontSize="sm">{dataHora(c.criada_em)}</Text>
                                            <Text fontSize="xs" color="gray.500">{c.criada_por || 'automático'}</Text>
                                        </Td>
                                        <Td>
                                            <Text fontSize="sm">
                                                {c.finalizada_em ? `respondida ${data(c.finalizada_em)}` : `até ${data(c.expira_em)}`}
                                            </Text>
                                        </Td>
                                        <Td isNumeric>{acoes(c)}</Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </TableContainer>
                )}

                {itens.length > 0 && (
                    <Flex p={3} borderTopWidth="1px" borderColor="gray.100" align="center" justify="space-between">
                        <Text fontSize="sm" color="gray.600">
                            Página {meta.pagina} de {meta.ultima_pagina} · {meta.total} consulta(s)
                        </Text>
                        <HStack spacing={1}>
                            <IconButton
                                aria-label="Anterior" icon={<FaChevronLeft />} size="sm" variant="ghost"
                                isDisabled={meta.pagina <= 1} onClick={() => setPagina((p) => p - 1)}
                            />
                            <IconButton
                                aria-label="Próxima" icon={<FaChevronRight />} size="sm" variant="ghost"
                                isDisabled={meta.pagina >= meta.ultima_pagina} onClick={() => setPagina((p) => p + 1)}
                            />
                        </HStack>
                    </Flex>
                )}
            </Box>

            <Drawer isOpen={!!detalhe} placement="right" size="lg" onClose={() => setDetalhe(null)}>
                <DrawerOverlay />
                <DrawerContent>
                    <DrawerCloseButton />
                    <DrawerHeader borderBottomWidth="1px">
                        <Text fontSize="md">Consulta #{detalhe?.id} · {detalhe?.fornecedor}</Text>
                        <Text fontSize="xs" color="gray.500" fontWeight="normal">
                            {dataHora(detalhe?.criada_em)} · {detalhe?.criada_por || 'automático'}
                        </Text>
                    </DrawerHeader>
                    <DrawerBody px={4} py={4}>
                        {carregandoDetalhe ? (
                            <Flex justify="center" py={10}><Spinner color="orange.400" /></Flex>
                        ) : !detalhe?.itens ? (
                            <Text color="gray.500">Não foi possível carregar os itens.</Text>
                        ) : (
                            <VStack align="stretch" spacing={0} divider={<Divider />}>
                                {detalhe.itens.map((i) => (
                                    <Box key={i.produto_id} py={3}>
                                        <Text fontSize="sm" fontWeight="medium" color="gray.800">{i.nome}</Text>
                                        <HStack mt={1} spacing={2} wrap="wrap">
                                            {i.codigo_fornecedor && (
                                                <Badge colorScheme="orange" variant="subtle" borderRadius="md">
                                                    {i.codigo_fornecedor}
                                                </Badge>
                                            )}
                                            {!i.resposta && <Text fontSize="xs" color="gray.400">sem resposta</Text>}
                                            {i.resposta === 'tem' && (
                                                <Badge colorScheme="green" borderRadius="md">tem {i.quantidade}</Badge>
                                            )}
                                            {i.resposta === 'nao_tem' && (
                                                <Badge colorScheme="red" borderRadius="md">não tem mais</Badge>
                                            )}
                                            {i.resultado && RESULTADO[i.resultado] && (
                                                <Badge
                                                    colorScheme={RESULTADO[i.resultado].cor}
                                                    variant="outline" borderRadius="md"
                                                >
                                                    {RESULTADO[i.resultado].rotulo}
                                                </Badge>
                                            )}
                                        </HStack>
                                    </Box>
                                ))}
                            </VStack>
                        )}
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        </>
    );
}
